import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { CoachNotes, PdfButton } from "@/components/admin/CoachingActions";
import { CallbackPicker, ScoreBadge, isDue } from "@/components/admin/CoachingFollowUp";
import { PaymentBlock } from "@/components/admin/CoachingPayment";

const SORTS = [["recenti", "Più recenti"], ["pronti", "Più pronti"], ["richiamo", "Data richiamo"]];
const sorters = {
  recenti: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
  pronti: (a, b) => (b.score ?? 0) - (a.score ?? 0),
  richiamo: (a, b) => (a.callback_date || "9999").localeCompare(b.callback_date || "9999"),
};

const STATUSES = [["da_chiamare", "Da chiamare", "#f0a78f"], ["sentito", "Sentito", "#e8d48b"], ["cliente", "Cliente", "#98bfa2"]];

const StatusPicker = ({ c, onChange }) => (
  <div className="mt-3 flex flex-wrap gap-2" data-testid={`coaching-status-${c.id}`}>
    {STATUSES.map(([k, label, color]) => (
      <button key={k} type="button" data-testid={`coaching-status-${c.id}-${k}`} onClick={() => onChange(c, { status: k })}
        className="rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-widest transition-colors"
        style={c.status === k ? { background: color, borderColor: color, color: "#050807" } : { borderColor: "rgba(255,255,255,0.15)", color: "#a3b1aa" }}>
        {label}
      </button>
    ))}
  </div>
);

const Item = ({ c, onChange, onLocal }) => (
  <div data-testid={`coaching-row-${c.id}`} className={`grid gap-3 border-l-4 p-4 md:grid-cols-[1fr_1.4fr] ${isDue(c) ? "border-l-[#e8d48b] bg-[#e8d48b]/5" : "border-l-transparent"}`}>
    <div className="min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="font-semibold">{c.name}</p>
        <ScoreBadge c={c} />
      </div>
      <p className="font-mono text-xs text-[#a3b1aa]"><a href={`mailto:${c.email}`} className="hover:text-[#c4dcca]">{c.email}</a> · <a href={`tel:${c.phone}`} className="hover:text-[#c4dcca]">{c.phone}</a></p>
      <p className="mt-1 font-mono text-[11px] text-[#a3b1aa]">
        {c.created_at ? new Date(c.created_at).toLocaleString("it-IT") : ""} · email {c.email_status === "sent" ? "inviata" : "non inviata"} · conferma {c.confirm_status === "sent" ? "inviata" : "non inviata"}
      </p>
      <StatusPicker c={c} onChange={onChange} />
      <CallbackPicker c={c} onChange={onChange} />
      <PaymentBlock c={c} onUpdated={onLocal} />
      <PdfButton c={c} />
      <CoachNotes c={c} onSave={onChange} />
    </div>
    <details className="text-sm text-[#a3b1aa]" data-testid={`coaching-answers-${c.id}`}>
      <summary className="cursor-pointer font-mono text-xs uppercase tracking-widest text-[#c4dcca]">
        {(c.answers || []).find((a) => a.label === "Obiettivo principale")?.value || "Anamnesi"} · vedi anamnesi
      </summary>
      <div className="mt-3 space-y-1">
        {(c.answers || []).map((a, i, arr) => (
          <div key={a.label}>
            {a.section !== arr[i - 1]?.section && <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-[#98bfa2]">{a.section}</p>}
            <p className="whitespace-pre-line"><span className="text-[#f4f7f5]">{a.label}:</span> {a.value}</p>
          </div>
        ))}
      </div>
    </details>
  </div>
);

export const CoachingPanel = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("recenti");
  useEffect(() => {
    api.get("/admin/coaching-checkups").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);
  const onLocal = (c, patch) => setItems((list) => list.map((x) => (x.id === c.id ? { ...x, ...patch } : x)));
  const change = async (c, patch) => {
    try {
      const r = await api.patch(`/admin/coaching-checkups/${c.id}`, patch);
      setItems((list) => list.map((x) => (x.id === c.id ? { ...x, ...patch, notes_updated_at: r.data?.notes_updated_at ?? x.notes_updated_at } : x)));
      if ("coach_notes" in patch) toast.success("Note salvate");
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const due = items.filter(isDue);
  const base = filter === "oggi" ? due : filter ? items.filter((c) => c.status === filter) : items;
  const shown = [...base].sort(sorters[sort]);
  return (
    <div data-testid="coaching-panel">
      {due.length > 0 && (
        <button type="button" data-testid="coaching-due-banner" onClick={() => setFilter("oggi")}
          className="mb-4 w-full border border-[#e8d48b]/50 bg-[#e8d48b]/10 px-4 py-3 text-left text-sm text-[#f4f7f5] hover:bg-[#e8d48b]/20">
          Oggi hai <span className="font-semibold text-[#e8d48b]">{due.length} {due.length === 1 ? "contatto" : "contatti"}</span> da richiamare (inclusi i richiami scaduti). Clicca per vederli.
        </button>
      )}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap gap-2" data-testid="coaching-filters">
        {[["", "Tutti"], ["oggi", "Da sentire oggi"], ...STATUSES].map(([k, label]) => (
          <button key={k || "all"} type="button" data-testid={`coaching-filter-${k || "tutti"}`} onClick={() => setFilter(k)}
            className={`rounded-full border px-4 py-1.5 font-mono text-xs uppercase tracking-widest ${filter === k ? "border-[#c4dcca] bg-[#c4dcca] text-[#050807]" : "border-white/15 text-[#a3b1aa]"}`}>
            {label} · {k === "oggi" ? due.length : k ? items.filter((c) => c.status === k).length : items.length}
          </button>
        ))}
      </div>
        <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#a3b1aa]">
          Ordina
          <select data-testid="coaching-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-white/15 bg-[#0b120e] px-2 py-1.5 text-[#f4f7f5]">
            {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
      </div>
      {shown.length === 0 && <p data-testid="coaching-empty" className="text-sm text-[#a3b1aa]">Nessun «Primo passo» ricevuto.</p>}
      <div className="divide-y divide-white/5 border border-[rgba(152,191,162,0.15)]">
        {shown.map((c) => <Item key={c.id} c={c} onChange={change} onLocal={onLocal} />)}
      </div>
    </div>
  );
};
